/**
 * 作業対象を選択 — V3。ドロップダウンを置き換えるモーダル。
 * プロジェクトと一般作業を分けて表示し、完了済みは既定で非表示。
 * キーボード優先（↑↓ で移動 / Enter で決定 / Esc で閉じる）。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlass, Folder, Tag, Plus } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

const PROJ_STATUS = {
  active: ['進行中', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  in_progress: ['進行中', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  planning: ['計画中', 'bg-sky-50 text-sky-700 border-sky-200'],
  on_hold: ['保留', 'bg-amber-50 text-amber-700 border-amber-200'],
  paused: ['保留', 'bg-amber-50 text-amber-700 border-amber-200'],
  completed: ['完了', 'bg-zinc-100 text-zinc-500 border-zinc-200'],
  done: ['完了', 'bg-zinc-100 text-zinc-500 border-zinc-200'],
};

// `is_finished` (canonical, stored) wins over `status` (deprecated for
// lifecycle — see services/project_lifecycle.py). Accepts the project object;
// the old signature took the bare `status` string, which could never show 完了
// for a finished project because this endpoint never rewrote that field.
export const projectStatusBadge = (p) => {
  if (!p) return null;
  if (p.is_finished === true) return PROJ_STATUS.completed;
  return PROJ_STATUS[p.status] || null;
};

export default function EngWorkTargetDialog({ open, onClose, meta, onPick, onCreateTitle, recent = [] }) {
  const [q, setQ] = useState('');
  const [showDone, setShowDone] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) { setQ(''); setCursor(0); setShowDone(false); setTimeout(() => inputRef.current?.focus(), 60); }
  }, [open]);

  const { projects, titles, flat } = useMemo(() => {
    const s = q.trim().toLowerCase();
    // Finished is the one canonical stored field, resolved by the backend
    // (`services/project_lifecycle.py`). This used to test `p.status`, which
    // nothing ever wrote, so finished projects were offered for new reports.
    const isDone = (p) => p.is_finished === true;
    const ps = (meta?.projects_all || meta?.projects || [])
      .filter((p) => showDone || !isDone(p))
      .filter((p) => !s || (p.name || '').toLowerCase().includes(s));
    const ts = (meta?.custom_titles || [])
      .filter((t) => !s || (t.title || '').toLowerCase().includes(s));
    return {
      projects: ps, titles: ts,
      flat: [...ps.map((p) => ({ kind: 'project', ...p })), ...ts.map((t) => ({ kind: 'title', ...t }))],
    };
  }, [meta, q, showDone]);

  const choose = (row) => {
    if (!row) return;
    if (row.kind === 'project') onPick({ type: 'project', id: row.id, label: row.name, status: row.status });
    else onPick({ type: 'title', id: row.id, label: row.title });
    onClose?.();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(flat.length - 1, c + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(flat[cursor]); }
  };

  const Row = ({ row, idx, icon }) => (
    <button type="button" onClick={() => choose(row)} onMouseEnter={() => setCursor(idx)}
      data-testid={`work-target-opt-${row.kind}-${row.id}`}
      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left transition-colors ${
        idx === cursor ? 'bg-teal-50 ring-1 ring-teal-300' : 'hover:bg-zinc-50'}`}>
      {icon}
      <span className="flex-1 truncate text-sm text-zinc-800">{row.kind === 'project' ? row.name : row.title}</span>
      {row.kind === 'project' && projectStatusBadge(row) && (
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${projectStatusBadge(row)[1]}`}
          data-testid={`work-target-badge-${row.id}`}>
          {projectStatusBadge(row)[0]}
        </span>
      )}
    </button>
  );

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden" aria-describedby={undefined}
        data-testid="work-target-dialog" onKeyDown={onKeyDown}>
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-lg font-medium">今日は何に取り組みましたか？</DialogTitle>
        </DialogHeader>
        <div className="px-4 pb-2">
          <div className="relative">
            <MagnifyingGlass size={15} className="absolute left-2.5 top-2.5 text-zinc-400" />
            <Input ref={inputRef} value={q} onChange={(e) => { setQ(e.target.value); setCursor(0); }}
              placeholder="プロジェクト名・作業名で検索（↑↓ で移動・Enter で決定）"
              className="h-9 pl-8 text-sm" data-testid="work-target-search" />
          </div>
          <label className="mt-2 flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)}
              className="accent-teal-600" data-testid="work-target-show-done" />
            完了済みプロジェクトを表示
          </label>
        </div>

        <div className="max-h-[52vh] overflow-y-auto px-2 pb-3 space-y-3">
          {!q.trim() && recent.length > 0 && (
            <div data-testid="work-target-recent">
              <p className="px-2.5 py-1 text-xs font-medium tracking-wider text-zinc-400">最近使用した作業</p>
              <div className="flex flex-wrap gap-1.5 px-2.5">
                {recent.slice(0, 6).map((r) => (
                  <button key={`${r.type}-${r.id}`} type="button"
                    onClick={() => choose({ kind: r.type === 'project' ? 'project' : 'title', ...r, name: r.label, title: r.label })}
                    data-testid={`work-target-recent-${r.id}`}
                    className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2.5 py-1 text-[11.5px] text-zinc-700 hover:border-teal-300 hover:bg-teal-50 transition-colors">
                    {r.type === 'project'
                      ? <Folder size={12} weight="duotone" className="text-teal-500" />
                      : <Tag size={12} weight="duotone" className="text-violet-500" />}
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className="px-2.5 py-1 text-xs font-medium tracking-wider text-zinc-400">プロジェクト</p>
            {projects.length === 0 ? (
              <p className="px-2.5 py-2 text-xs text-zinc-400">該当なし</p>
            ) : projects.map((p, i) => (
              <Row key={p.id} row={{ kind: 'project', ...p }} idx={i}
                icon={<Folder size={14} weight="duotone" className="text-teal-500 shrink-0" />} />
            ))}
          </div>
          <div>
            <p className="px-2.5 py-1 text-xs font-medium tracking-wider text-zinc-400">一般作業</p>
            {titles.length === 0 ? (
              <p className="px-2.5 py-2 text-xs text-zinc-400">該当なし</p>
            ) : titles.map((t, i) => (
              <Row key={t.id} row={{ kind: 'title', ...t }} idx={projects.length + i}
                icon={<Tag size={14} weight="duotone" className="text-violet-500 shrink-0" />} />
            ))}
            {q.trim() && onCreateTitle && (
              <Button variant="outline" size="sm" onClick={() => { onCreateTitle(q.trim()); onClose?.(); }}
                className="mt-1.5 ml-1 h-7 text-[11px]" data-testid="work-target-create">
                <Plus size={12} className="mr-1" />「{q.trim()}」を一般作業として追加
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
